'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Check, Circle, Loader2, Rocket, Undo2, Redo2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { MaterialSpinner } from '@/components/ui/material-spinner';
import { usePageBuilderContext } from '../page-builder-context';
import { pageBuilderFormSchema, validateAllBlocks } from '@/lib/validations/page-builder';
import { cn } from '@/lib/utils';

const SUBMIT_DELAY_MS = 2500;

// ============================================================================
// DRAFT INDICATOR
// ============================================================================

function DraftIndicator() {
  const { isDirty, isSaving } = usePageBuilderContext();

  return (
    <AnimatePresence mode="wait">
      {isSaving ? (
        <motion.div
          key="saving"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="flex items-center gap-1.5 text-xs text-foreground-tertiary"
        >
          <Loader2 className="h-3 w-3 animate-spin" />
          <span>Сохраняем...</span>
        </motion.div>
      ) : isDirty ? (
        <motion.div
          key="dirty"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="flex items-center gap-1.5 text-xs text-foreground-tertiary"
        >
          <div className="w-1.5 h-1.5 rounded-full bg-warning" />
          <span>Не сохранено</span>
        </motion.div>
      ) : (
        <motion.div
          key="saved"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="flex items-center gap-1.5 text-xs text-success"
        >
          <Check className="h-3 w-3" />
          <span>Сохранено</span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ============================================================================
// UNDO/REDO BUTTONS
// ============================================================================

function UndoRedoButtons() {
  const { undo, redo, canUndo, canRedo } = usePageBuilderContext();

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={undo}
        disabled={!canUndo}
        className={cn(
          'p-2 rounded-lg transition-colors',
          canUndo
            ? 'text-foreground-secondary hover:text-foreground hover:bg-muted'
            : 'text-foreground-tertiary/50 cursor-not-allowed'
        )}
        aria-label="Отменить"
      >
        <Undo2 className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={redo}
        disabled={!canRedo}
        className={cn(
          'p-2 rounded-lg transition-colors',
          canRedo
            ? 'text-foreground-secondary hover:text-foreground hover:bg-muted'
            : 'text-foreground-tertiary/50 cursor-not-allowed'
        )}
        aria-label="Повторить"
      >
        <Redo2 className="h-4 w-4" />
      </button>
    </div>
  );
}

// ============================================================================
// READINESS CHECKLIST
// ============================================================================

interface ChecklistItem {
  label: string;
  done: boolean;
  scrollTarget: string;
}

function scrollToSection(id: string) {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

function ReadinessChecklist({ items }: { items: ChecklistItem[] }) {
  return (
    <div className="flex items-center gap-1.5">
          {items.map((item) => (
            <button
              key={item.scrollTarget}
              type="button"
              onClick={() => {
                if (!item.done) scrollToSection(item.scrollTarget);
              }}
              className={cn(
                'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap',
                'transition-colors duration-150',
                item.done
                  ? 'text-success bg-success/8'
                  : 'text-foreground-secondary bg-muted/60 hover:bg-muted cursor-pointer'
              )}
            >
              <AnimatePresence mode="wait" initial={false}>
                {item.done ? (
                  <motion.div
                    key="check"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                  >
                    <Check className="h-3 w-3" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="circle"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                  >
                    <Circle className="h-3 w-3" />
                  </motion.div>
                )}
              </AnimatePresence>
              <span>{item.label}</span>
            </button>
          ))}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function ActionBar() {
  const { submit, isSubmitting, validate, formData, hasActiveUploads, mode } = usePageBuilderContext();
  const [isPreparing, setIsPreparing] = useState(false);

  const isBusy = isPreparing || isSubmitting;
  const isEdit = mode === 'edit';

  // Each chip validates its slice of the REAL submit schema, not just field
  // presence. Presence-only checks let invalid stored data (over-long
  // description, a website that isn't a full URL, a bad slug, >3 categories)
  // show green and then fail validate() in handleSubmit with no visible reason —
  // which is why editing an existing product could error on save with every
  // chip green. Now a green chip guarantees that section will pass submit, so
  // all three green ⟺ save proceeds.
  const hasBasicInfo = pageBuilderFormSchema.shape.header.safeParse(formData.header).success;
  const hasBlocks =
    pageBuilderFormSchema.shape.blocks.safeParse(formData.blocks).success &&
    validateAllBlocks(formData.blocks).size === 0;
  const hasMetadata = pageBuilderFormSchema.shape.metadata.safeParse(formData.metadata).success;
  const canSubmit = hasBasicInfo && hasBlocks && hasMetadata;

  const checklistItems: ChecklistItem[] = [
    { label: 'Основное', done: hasBasicInfo, scrollTarget: 'section-header' },
    { label: 'Контент', done: hasBlocks, scrollTarget: 'section-content' },
    { label: 'Категории', done: hasMetadata, scrollTarget: 'section-metadata' },
  ];

  const handleSubmit = async () => {
    if (!canSubmit) {
      const firstIncomplete = checklistItems.find((i) => !i.done);
      if (firstIncomplete) {
        scrollToSection(firstIncomplete.scrollTarget);
      }
      return;
    }
    if (validate()) {
      setIsPreparing(true);
      await new Promise((r) => setTimeout(r, SUBMIT_DELAY_MS));
      setIsPreparing(false);
      await submit();
    } else {
      // Never fail silently: validate() populates the error map, but some error
      // keys (e.g. image display URLs) don't map to a visible field, which would
      // otherwise leave the button looking completely unresponsive.
      toast.error('Исправьте ошибки перед сохранением');
    }
  };

  return (
    <div className="sticky bottom-4 z-30">
      <div
        className={cn(
          'flex items-center justify-between gap-4 p-4',
          'bg-card/95 backdrop-blur-md border border-border rounded-xl shadow-lg'
        )}
      >
        {/* Left side: Draft status + Undo/Redo */}
        <div className="flex items-center gap-4">
          <UndoRedoButtons />
          <div className="w-px h-6 bg-border" />
          <DraftIndicator />
        </div>

        {/* Center: Readiness checklist */}
        <ReadinessChecklist items={checklistItems} />

        {/* Right side: Submit button */}
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={isBusy || hasActiveUploads}
          size="lg"
          className={cn(
            'min-w-[160px] font-bold gap-2',
            !canSubmit && !isBusy && 'opacity-50'
          )}
        >
          {isBusy ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              {isEdit ? 'Сохраняем...' : 'Отправляем...'}
            </>
          ) : hasActiveUploads ? (
            <>
              <MaterialSpinner className="h-5 w-5 text-current" />
              Загрузка фото...
            </>
          ) : isEdit ? (
            <>
              <Check className="h-5 w-5" />
              Сохранить изменения
            </>
          ) : (
            <>
              <Rocket className="h-5 w-5" />
              Запустить
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
